// src/pages/SignUp.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { GoogleLogin } from '@react-oauth/google';
import { validateField } from "../utils/helpers/validate";
import NavBar from "../components/layout/NavBar";
import { API_CONSTANT_MAP } from "../utils/constants/endpoints";
import { request } from "../services/networking/request";
import { signup } from "../store/actions";
import Loader from "../components/Loading";
import OuterContainer from "../components/layout/OuterContainer";
import TextInput from "../components/common/TextInput";
import Button from "../components/common/Button";
import styled from "styled-components";
import Meta from "../components/Meta";
import colors from "../utils/constants/colors";
import useAnalytics from "../hooks/useAnalytics";

/* =============================== Styling only =============================== */

const AuthBg = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #f7f9fc 0%, #f1f5fb 60%, #eef3fb 100%);
  padding-top: 64px; /* space for NavBar */
`;

const SignUpContainer = styled.div`
  background: #ffffff;
  border: 1px solid #e9eef6;
  border-radius: 16px;
  margin: 48px auto;
  width: 480px;
  max-width: 95%;
  padding: 36px 32px;
  box-shadow: 0 12px 28px rgba(2, 8, 23, 0.06), 0 2px 8px rgba(2, 8, 23, 0.04);
`;

const Header = styled.h1`
  margin: 0 0 6px 0;
  text-align: center;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: 0.2px;
  color: #0f172a;
`;

const SubHeader = styled.p`
  margin: 0 0 24px 0;
  text-align: center;
  font-size: 14px;
  color: #64748b;
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 18px;
  font-size: 14px;
  color: #475569;

  .link {
    color: ${colors.primary};
    font-weight: 700;
    cursor: pointer;
    margin-left: 6px;
  }
`;

const ErrorMessage = styled.div`
  margin: 8px 0 12px;
  font-size: 13px;
  color: #b91c1c;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  padding: 8px 12px;
`;

const Divider = styled.div`
  height: 1px;
  background: #eef2f7;
  margin: 18px 0 8px;
`;

const GoogleSignInWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 20px 0;
  width: 100%;
  
  /* Ensure the wrapper container stays centered */
  > div {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
  }
  
  /* Prevent Google button from overflowing or shifting */
  iframe,
  button,
  [role="button"],
  [data-testid] {
    margin: 0 auto !important;
    position: relative !important;
    left: 0 !important;
    right: 0 !important;
    transform: none !important;
  }
  
  /* Container flexibility for Google's dynamic sizing */
  .g_id_signin {
    display: flex !important;
    justify-content: center !important;
    margin: 0 auto !important;
    max-width: 100% !important;
  }
`;

const OrDivider = styled.div`
  display: flex;
  align-items: center;
  margin: 24px 0;
  color: #64748b;
  font-size: 14px;
  
  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e2e8f0;
  }
  
  &::before {
    margin-right: 16px;
  }
  
  &::after {
    margin-left: 16px;
  }
`;

const VerificationContainer = styled.div`
  text-align: center;
  padding: 12px 0;
  
  .mail-icon {
    width: 48px;
    height: 48px;
    background: #f0f9ff;
    border: 2px solid #e0f2fe;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
    font-size: 18px;
    color: ${colors.primary};
  }
  
  h2 {
    margin: 0 0 8px;
    color: #0f172a;
    font-size: 1.2rem;
    font-weight: 600;
  }
  
  p {
    margin: 0 0 12px;
    color: #64748b;
    font-size: 0.9rem;
    line-height: 1.4;
  }
  
  .email-highlight {
    color: ${colors.primary};
    font-weight: 500;
  }
  
  .helper-text {
    font-size: 0.85rem;
    color: #94a3b8;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #f1f5f9;
  }
`;

/* =============================== Component =============================== */

const SignUp = (props) => {
  const navigate = useNavigate();
  const { trackSignup, trackError } = useAnalytics();
  
  const initialState = {
    first_name: "",
    last_name: "",
    email: "",
    password: "",
  };
  const [formValues, setFormValues] = useState(initialState);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [isVerificationSent, setIsVerificationSent] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((v) => ({ ...v, [name]: value }));
    setErrorMsg(""); // Clear error when user types

    const errors = validateField(name, value);
    setFormErrors((prev) => ({
      ...prev,
      [name]: errors ? errors : null,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate(formValues);
    setFormErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    setErrorMsg("");

    try {
      // Use the new signup_with_verification endpoint
      const endpoint = API_CONSTANT_MAP.signup_with_verification;
      const result = await request("POST", endpoint, formValues);
      
      if (result.data?.success) {
        // Success - verification email sent
        setIsVerificationSent(true);
        setSubmittedEmail(formValues.email);
        
        // Track successful signup
        trackSignup('email');
      } else {
        const errorMessage = result.data?.error?.message || "Signup failed. Please try again.";
        setErrorMsg(errorMessage);
        
        // Track signup failure
        trackError(errorMessage, 'signup_form');
      }
    } catch (error) {
      console.error('Signup error:', error);
      const errorMessage = error.error?.message || "Something went wrong. Please try again.";
      setErrorMsg(errorMessage);
      
      // Track signup error
      trackError(errorMessage, 'signup_api');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleHaveAccount = (e) => {
    e.preventDefault();
    navigate("/signin");
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setIsSubmitting(true);
      props.signup({ type: "SIGNUP_INIT" });

      const endpoint = API_CONSTANT_MAP.google_signin;
      const result = await request("POST", endpoint, {
        googleToken: credentialResponse.credential,
      });

      if ("token" in result.data) {
        trackSignup('google');
        props.signup({ type: "SIGNUP_SUCCESS", payload: result.data });
        
        // Check if user needs onboarding
        const onboardingStatus = result.data.user.onboarding_status;
        if (onboardingStatus === 'pending') {
          // User needs onboarding - redirect there
          const urlParams = new URLSearchParams(window.location.search);
          const redirectTo = urlParams.get('redirect');
          const onboardingUrl = redirectTo ? `/onboarding?redirect=${encodeURIComponent(redirectTo)}` : "/onboarding";
          navigate(onboardingUrl);
        } else {
          // User has already completed or skipped onboarding - proceed normally
          const urlParams = new URLSearchParams(window.location.search);
          const redirectTo = urlParams.get('redirect');
          navigate(redirectTo || "/");
        }
      }
    } catch (response) {
      if (response.error) setErrorMsg(response.error.message);
      trackError('google_signup_failed', response.error?.message);
      props.signup({ type: "SIGNUP_FAILURE" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleError = () => {
    setErrorMsg("Google Sign-Up failed. Please try again.");
    trackError('google_signup_error', 'Google authentication failed');
  };

  const handleResendVerification = async () => {
    setIsResending(true);
    setResendSuccess(false);

    try {
      const endpoint = API_CONSTANT_MAP.signup_with_verification;
      const result = await request("POST", endpoint, formValues);

      if (result.data?.success) {
        setResendSuccess(true);
        // Reset success message after 5 seconds
        setTimeout(() => setResendSuccess(false), 5000);
      } else {
        setErrorMsg(result.data?.error?.message || "Failed to resend. Please try again.");
      }
    } catch (error) {
      console.error('Resend error:', error);
      setErrorMsg("Failed to resend verification email. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  const validate = (values) => {
    const errors = {};
    const first_name = validateField("name", values.first_name);
    const last_name = validateField("name", values.last_name);
    const email = validateField("email", values.email);
    const password = validateField("password", values.password);

    if (first_name) errors.first_name = first_name;
    if (last_name) errors.last_name = last_name;
    if (email) errors.email = email;
    if (password) errors.password = password;

    return errors;
  };

  return (
    <Meta pageMeta={{ title: "Sign up - DoMyTask" }}>
      <NavBar user={props.state.user} />
      <AuthBg>
        <OuterContainer>
          <SignUpContainer>
            {isVerificationSent ? (
              // Verification Email Sent State
              <VerificationContainer>
                <div className="mail-icon">
                  ✉️
                </div>
                <h2>Verification email sent</h2>
                <p>
                  Check your inbox at <span className="email-highlight">{submittedEmail}</span> and click the verification link to complete your signup.
                </p>
                {resendSuccess && (
                  <p style={{ color: '#10b981', fontWeight: 500, marginTop: '12px' }}>
                    ✓ Verification email resent successfully!
                  </p>
                )}
                {errorMsg && (
                  <p style={{ color: '#ef4444', marginTop: '12px' }}>
                    {errorMsg}
                  </p>
                )}
                <div className="helper-text">
                  <p style={{ marginBottom: '12px' }}>
                    Didn't receive it? Check your spam folder or click below to resend.
                  </p>
                  <Button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={isResending}
                    style={{
                      background: 'transparent',
                      color: colors.primary,
                      border: `1px solid ${colors.primary}`,
                      padding: '10px 20px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: isResending ? 'not-allowed' : 'pointer',
                      opacity: isResending ? 0.7 : 1,
                    }}
                  >
                    {isResending ? 'Sending...' : 'Resend verification email'}
                  </Button>
                </div>
              </VerificationContainer>
            ) : (
              // Signup Form State
              <>
                <Header>Create your Doitly account</Header>
                <SubHeader>
                  Join the marketplace and get things done faster
                </SubHeader>

                {/* Google Sign-Up */}
                <GoogleSignInWrapper>
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    theme="outline"
                    size="large"
                    text="signup_with"
                    width="100%"
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      textAlign: 'center',
                      margin: '0 auto'
                    }}
                  />
                </GoogleSignInWrapper>

                <OrDivider>Or continue with email</OrDivider>

                <form onSubmit={handleSubmit}>
              <TextInput
                value={formValues.first_name}
                name={"first_name"}
                label={"First Name"}
                onChange={handleChange}
              />
              {formErrors.first_name && (
                <ErrorMessage>{formErrors.first_name}</ErrorMessage>
              )}

              <TextInput
                value={formValues.last_name}
                name={"last_name"}
                label={"Last Name"}
                onChange={handleChange}
              />
              {formErrors.last_name && (
                <ErrorMessage>{formErrors.last_name}</ErrorMessage>
              )}

              <TextInput
                value={formValues.email}
                name={"email"}
                label={"Email address"}
                onChange={handleChange}
                type={"text"}
              />
              {formErrors.email && (
                <ErrorMessage>{formErrors.email}</ErrorMessage>
              )}

              <TextInput
                value={formValues.password}
                name={"password"}
                label={"Password"}
                onChange={handleChange}
                type={"password"}
                placeholder={"Use at least 6 characters"}
              />
              {formErrors.password && (
                <ErrorMessage>{formErrors.password}</ErrorMessage>
              )}

              <Button
                type="submit"
                disabled={isSubmitting}
                style={{
                  margin: "18px 0 10px",
                  background:
                    "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
                  borderRadius: "10px",
                  fontWeight: 800,
                  opacity: isSubmitting ? 0.7 : 1,
                }}
              >
                <span>{isSubmitting ? "Creating Account..." : "Create Account"}</span>
              </Button>

              {errorMsg && <ErrorMessage>{errorMsg}</ErrorMessage>}

              <Divider />

                  <Footer>
                    <span>Already have an account?</span>
                    <span className="link" onClick={handleHaveAccount}>
                      Sign in
                    </span>
                  </Footer>
                </form>
              </>
            )}
          </SignUpContainer>
        </OuterContainer>
      </AuthBg>
      <Loader animating={props.state.user.isLoading} />
    </Meta>
  );
};

const mapStateToProps = (state) => {
  return { state };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ signup }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(SignUp);

// import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import { connect } from "react-redux";
// import { bindActionCreators } from "redux";
// import { validateField } from "../utils/helpers/validate";
// import NavBar from "../components/layout/NavBar";
// import { API_CONSTANT_MAP } from "../utils/constants/endpoints";
// import { request } from "../services/networking/request";
// import { signup } from "../store/actions";
// import Loader from "../components/Loading";
// import OuterContainer from "../components/layout/OuterContainer";
// import TextInput from "../components/common/TextInput";
// import Button from "../components/common/Button";
// import styled from "styled-components";
// import Meta from "../components/Meta";
// import colors from "../utils/constants/colors"; // Import colors to reflect DoMyTask theme

// const SignUpContainer = styled.div`
//   background: #fff;
//   border: 1px solid ${colors.greyLight};
//   border-radius: 8px;
//   display: block;
//   flex-shrink: 0;
//   margin: 0 auto;
//   min-height: 0;
//   width: 450px;
//   max-width: 98%;
//   padding: 48px 40px 36px;
// `;

// const Header = styled.h1`
//   padding: 10px 0px;
//   color: ${colors.primary};
//   margin-bottom: 0px;
//   margin-top: 10px;
//   display: flex;
//   justify-content: center;
//   width: 100%;
//   font-size: 1.5rem;
//   font-weight: 700;
//   cursor: pointer;
//   &:hover {
//     text-decoration: underline;
//   }
// `;

// const Footer = styled.div`
//   display: flex;
//   justify-content: space-between;
//   align-items: center;
//   margin: 20px 0px 0px 0px;

//   & > span {
//     font-size: 1rem;
//     padding: 5px;
//     font-weight: 500;
//   }

//   & .link {
//     color: ${colors.primaryDarker};
//     cursor: pointer;
//     font-weight: 500;
//   }
// `;

// const ErrorMessage = styled.div`
//   color: red;
//   font-size: 12px;
//   margin: 3px 0px 10px 0px;
// `;

// const SignUp = (props) => {
//   const navigate = useNavigate();
//   const initialState = {
//     first_name: "",
//     last_name: "",
//     email: "",
//     password: "",
//   };
//   const [formValues, setFormValues] = useState(initialState);
//   const [formErrors, setFormErrors] = useState({});
//   const [isSubmit, setIsSubmit] = useState(false);
//   const [errorMsg, setErrorMsg] = useState();

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormValues({
//       ...formValues,
//       [name]: value,
//     });

//     const errors = validateField(name, value);
//     setFormErrors((prevErrors) => ({
//       ...prevErrors,
//       [name]: errors ? errors : null,
//     }));
//   };

//   useEffect(() => {
//     if (isSubmit) {
//       props.signup({ type: "SIGNUP_INIT" });

//       const endpoint = API_CONSTANT_MAP.signup;
//       const result = request("POST", endpoint, formValues);

//       result
//         .then((result) => {
//           if ("token" in result.data) {
//             props.signup({
//               type: "SIGNUP_SUCCESS",
//               payload: result.data,
//             });
//             navigate("/");
//           }
//         })
//         .catch((response) => {
//           if (response.error) {
//             setErrorMsg(response.error.message);
//           }
//           props.signup({
//             type: "SIGNUP_FAILURE",
//           });
//         });
//     }
//   }, [isSubmit]);

//   const handleSubmit = (e) => {
//     e.preventDefault();
//     const errors = validate(formValues);
//     setFormErrors(errors);

//     if (Object.keys(errors).length === 0) {
//       setIsSubmit(!isSubmit);
//     }
//   };

//   const handleHaveAccount = (e) => {
//     e.preventDefault();
//     navigate("/signin");
//   };

//   const validate = (values) => {
//     const errors = {};
//     const first_name = validateField("name", values.first_name);
//     const last_name = validateField("name", values.last_name);
//     const email = validateField("email", values.email);
//     const password = validateField("password", values.password);

//     if (first_name) {
//       errors.first_name = first_name;
//     }
//     if (last_name) {
//       errors.last_name = last_name;
//     }
//     if (email) {
//       errors.email = email;
//     }
//     if (password) {
//       errors.password = password;
//     }

//     return errors;
//   };

//   return (
//     <Meta
//       pageMeta={{
//         title: "Sign up - DoMyTask",
//       }}
//     >
//       <NavBar user={props.state.user} />
//       <OuterContainer>
//         <SignUpContainer>
//           <Header>
//             <span>Welcome to Taskly</span>
//           </Header>
//           <form onSubmit={handleSubmit}>
//             <TextInput
//               value={formValues.first_name}
//               name={"first_name"}
//               label={"First Name"}
//               onChange={handleChange}
//             />
//             {formErrors.first_name && (
//               <ErrorMessage>{formErrors.first_name}</ErrorMessage>
//             )}
//             <TextInput
//               value={formValues.last_name}
//               name={"last_name"}
//               label={"Last Name"}
//               onChange={handleChange}
//             />
//             {formErrors.last_name && (
//               <ErrorMessage>{formErrors.last_name}</ErrorMessage>
//             )}
//             <TextInput
//               value={formValues.email}
//               name={"email"}
//               label={"Email address"}
//               onChange={handleChange}
//               type={"text"}
//             />
//             {formErrors.email && (
//               <ErrorMessage>{formErrors.email}</ErrorMessage>
//             )}
//             <TextInput
//               value={formValues.password}
//               name={"password"}
//               label={"Password"}
//               onChange={handleChange}
//               type={"password"}
//               placeholder={"Use at least 6 characters"}
//             />
//             {formErrors.password && (
//               <ErrorMessage>{formErrors.password}</ErrorMessage>
//             )}
//             <Button
//               type="submit"
//               style={{ margin: "20px 0px", backgroundColor: colors.primary }}
//             >
//               <span>Sign up</span>
//             </Button>
//             {errorMsg && <ErrorMessage>{errorMsg}</ErrorMessage>}
//             <Footer>
//               <span>
//                 Already have an account?{" "}
//                 <span class="link" onClick={handleHaveAccount}>
//                   Sign in
//                 </span>
//               </span>
//             </Footer>
//           </form>
//         </SignUpContainer>
//       </OuterContainer>
//       <Loader animating={props.state.user.isLoading} />
//     </Meta>
//   );
// };

// const mapStateToProps = (state) => {
//   return { state };
// };

// const mapDispatchToProps = (dispatch) =>
//   bindActionCreators({ signup }, dispatch);

// export default connect(mapStateToProps, mapDispatchToProps)(SignUp);
